/**
 * Composable pour la gestion des permissions.
 *
 * Accède directement au store Vuex sans passer par getCurrentInstance(),
 * ce qui le rend utilisable dans n'importe quel contexte (setup, composables, etc.).
 *
 * @example
 *   import { usePermissions } from '@/composables/usePermissions'
 *   import { P, ROLES } from '@/config/permissions'
 *
 *   const { can, canAll, hasRole, isSuperAdmin } = usePermissions()
 *
 *   // Vérifier une ou plusieurs permissions (OR)
 *   if (can([P.COMPTES.BROWSE])) { ... }
 *
 *   // Vérifier que TOUTES les permissions sont présentes (AND)
 *   if (canAll([P.COMPTES.EDIT, P.COMPTES.BROWSE])) { ... }
 *
 *   // Vérifier un rôle
 *   if (hasRole(ROLES.COMMERCIAL)) { ... }
 */
import { computed } from 'vue'
import { useStore } from 'vuex'
import { ROLES } from '@/config/permissions'

export function usePermissions() {
  const store = useStore()

  const permissions = computed(() => store.getters?.permissions || [])
  const roles = computed(() => store.getters?.roles || [])
  const isSuperAdmin = computed(() => roles.value.includes(ROLES.SUPER_ADMIN))
  // Côté calculé par le serveur (UserResource.scope). Le déduire du rôle nommé
  // « extranet client » rangeait du côté interne tout client portant un autre
  // profil extranet — et ce drapeau sert à lui FERMER le centre de notifications.
  const isExtranetClient = computed(() => store.getters?.isExtranet === true)

  /**
   * Vérifie si l'utilisateur possède les permissions demandées.
   * @param {string|string[]} perms - Permission(s) à vérifier
   * @param {Object} [options]
   * @param {'any'|'all'} [options.mode='any'] - 'any' (OR) ou 'all' (AND)
   * @returns {boolean}
   */
  const can = (perms, { mode = 'any' } = {}) => {
    const arr = Array.isArray(perms) ? perms : [perms]
    if (arr.length === 0) return false
    if (isSuperAdmin.value) return true
    const checker = mode === 'all' ? 'every' : 'some'
    return arr[checker](p => permissions.value.includes(p))
  }

  /**
   * Vérifie que l'utilisateur possède TOUTES les permissions (AND).
   */
  const canAll = (perms) => can(perms, { mode: 'all' })

  /**
   * Vérifie si l'utilisateur possède un rôle.
   */
  const hasRole = (role) => roles.value.includes(role)

  // ── Computed helpers self user ──
  // Les permissions "self" ont été supprimées côté backend (hardcodé dans les controllers).
  // Tout utilisateur authentifié peut voir/modifier son propre profil.
  const canEditSelf = computed(() => true)
  const canViewSelf = computed(() => true)
  const canEditSelfPassword = computed(() => true)
  const canEditSelfImage = computed(() => true)
  const canViewSelfImage = computed(() => true)
  const canDeleteSelfImage = computed(() => true)

  return {
    // State
    permissions,
    roles,
    isSuperAdmin,
    isExtranetClient,

    // Méthodes
    can,
    canAll,
    hasRole,

    // Rétrocompatibilité avec l'ancien composable
    checkPerms: can,
    checkRoles: (r) => {
      const arr = Array.isArray(r) ? r : [r]
      if (!arr.includes(ROLES.EXTRANET_CLIENT) && isSuperAdmin.value) return true
      return arr.some(role => roles.value.includes(role))
    },
    hasAnyPerm: can,
    hasAllPerms: canAll,

    // Computed helpers
    canEditSelf,
    canViewSelf,
    canEditSelfPassword,
    canEditSelfImage,
    canViewSelfImage,
    canDeleteSelfImage,
  }
}
